import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import {
    CompareContractsInput,
    CompareContractsOutput
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import { UnknownCoralError } from '@amzn/ihm-js-coral-client';
import leftArrowTokens from '@amzn/meridian-tokens/base/icon/arrow-left-large';
import rightArrowTokens from '@amzn/meridian-tokens/base/icon/arrow-right-large';
import Alert from '@amzn/meridian/alert';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Loader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';
import Tab, { TabGroup } from '@amzn/meridian/tab';
import Text from '@amzn/meridian/text';

import { isNil, isUndefined } from 'lodash';
import { contractConfigurationClient } from 'src/api/client';
import { CompareBySummary } from 'src/components/CompareContracts/CompareBySummary';
import { CompareByTable } from 'src/components/CompareContracts/CompareByTable';
import { ContractSelector, createDealMap } from 'src/components/CompareContracts/ContractSelector';
import { SideBySideViewer } from 'src/components/CompareContracts/SideBySideViewer';
import { validateComparison } from 'src/components/CompareContracts/utils/comparison-extractor';
import { countDifferences } from 'src/components/CompareContracts/utils/comparison-utils';
import {
    getErrorMessage,
    isRetryableError,
    shouldAutoFallback
} from 'src/components/CompareContracts/utils/error-handling';
import { getFullComparison } from 'src/components/CompareContracts/utils/paginator';
import { IdMappers, mapperHooks } from 'src/components/CompareContracts/utils/useIdMappers';
import { RateCardDetail } from 'src/components/RateCardDetail';
import { AppContext } from 'src/contexts/appContext';
import { apiHooks } from 'src/hooks/apiHooks';
import { useTitle } from 'src/hooks/useTitle';
import sparklesSmallIcon from 'src/icons/SparklesSmall';

const COMPARE_BY_SUMMARY = 'by-summary';
const COMPARE_BY_TABLE = 'by-table';
const COMPARE_SIDE_BY_SIDE = 'side-by-side';
const TABS = [COMPARE_BY_SUMMARY, COMPARE_BY_TABLE, COMPARE_SIDE_BY_SIDE] as const;
type ValidTab = (typeof TABS)[number];

export const CompareContracts: React.FC = () => {
    const { setToasts, setTopAlertMsg } = useContext(AppContext);

    // Separate state for summary vs detailed data
    const [summaryData, setSummaryData] = useState<CompareContractsOutput | null>(null);
    const [detailedData, setDetailedData] = useState<CompareContractsOutput>(
        CompareContractsOutput.clone({
            totalSize: 0,
            deltas: []
        })
    );
    const [summaryLoading, setSummaryLoading] = useState(false);
    const [summaryError, setSummaryError] = useState<string | null>(null);

    // Track inputs used for last successful API calls to prevent duplicate calls for both summary and detailed data
    const lastApiInputsRef = useRef<{
        leftId?: number;
        rightId?: number;
        leftTimestamp?: string;
        rightTimestamp?: string;
    } | null>(null);

    const differences = useMemo(() => countDifferences(detailedData), [detailedData]);
    const exactMatch = differences === 0;
    const [currentTab, setCurrentTab] = useState<ValidTab>(COMPARE_BY_SUMMARY);

    const [leftId, setLeftId] = useState<number | undefined>();
    const [rightId, setRightId] = useState<number | undefined>();
    const [leftTimestamp, setLeftTimestamp] = useState<string | undefined>();
    const [rightTimestamp, setRightTimestamp] = useState<string | undefined>();

    const [hasComparison, setHasComparison] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);

    const { response: entitiesResponse } = apiHooks.useApiCall('getEntitiesByIds', {
        includeIneligibleVendorCodes: false
    });

    const { response: dealsResponse, error: dealsError } = apiHooks.useApiCall('getContractDealsByIds', {
        includeDeletedContractDeals: false,
        includeDraftContracts: true,
        includeIneligibleContractDeals: false
    });
    const contractIdMap = useMemo(() => createDealMap(dealsResponse?.contractDeals), [dealsResponse]);
    useEffect(() => {
        if (!isUndefined(dealsError)) {
            setToasts((prev) => [
                ...prev,
                { type: 'error', message: 'Failed to fetch contract names.', id: 'deals-failure' }
            ]);
        }
    }, [dealsError]);

    useTitle(getTitle(leftId, rightId, contractIdMap));

    const formatters: IdMappers = mapperHooks.useIdMappers();

    // Helper function to compare inputs and determine if API calls need to be made
    const inputsChanged = useCallback(
        (currentInputs: { leftId?: number; rightId?: number; leftTimestamp?: string; rightTimestamp?: string }) => {
            if (!lastApiInputsRef.current) return true;

            return (
                lastApiInputsRef.current.leftId !== currentInputs.leftId ||
                lastApiInputsRef.current.rightId !== currentInputs.rightId ||
                lastApiInputsRef.current.leftTimestamp !== currentInputs.leftTimestamp ||
                lastApiInputsRef.current.rightTimestamp !== currentInputs.rightTimestamp
            );
        },
        []
    );

    const loadSummaryData = useCallback(() => {
        if (isNil(leftId) || isNil(rightId)) return;

        const currentInputs = { leftId, rightId, leftTimestamp, rightTimestamp };

        // Only call API if inputs have changed
        if (!inputsChanged(currentInputs)) {
            console.log('Summary inputs unchanged, using cached data');
            return;
        }

        lastApiInputsRef.current = currentInputs;
        setSummaryLoading(true);
        setSummaryError(null);

        const input = CompareContractsInput.clone({
            left: { contractId: leftId, timestamp: leftTimestamp },
            right: { contractId: rightId, timestamp: rightTimestamp },
            includeMatchingRows: false, // OPTIMIZATION: Only differences for LLM
            includeSummary: true // Request LLM summary
        });

        contractConfigurationClient.compareContracts(input).subscribe({
            next: (response) => {
                setSummaryData(response);

                if (!response.summary) {
                    const errorMsg = 'Summary generation failed. Please try the detailed view.';
                    setSummaryError(errorMsg);
                    setToasts((prev) => [
                        ...prev,
                        {
                            id: 'summary-missing',
                            type: 'warning',
                            message: 'Summary was not generated. Using detailed comparison instead.'
                        }
                    ]);
                    setCurrentTab(COMPARE_BY_TABLE);
                }
            },
            error: (error) => {
                const errorMessage = getErrorMessage(error);
                setSummaryError(errorMessage);
                // Reset lastApiInputs on error to allow retry
                lastApiInputsRef.current = null;

                // Show appropriate toast notification
                setToasts((prev) => [
                    ...prev,
                    {
                        id: 'summary-error',
                        type: 'warning',
                        message: isRetryableError(error)
                            ? 'Summary generation failed. You can try again or use the detailed view.'
                            : 'Summary generation failed. Showing detailed comparison instead.',
                        timeout: 8000
                    }
                ]);

                // Auto-fallback for non-retryable errors
                if (shouldAutoFallback(error)) {
                    setCurrentTab(COMPARE_BY_TABLE);
                }
            },
            complete: () => setSummaryLoading(false)
        });
    }, [leftId, rightId, leftTimestamp, rightTimestamp, inputsChanged, setToasts]);

    // Clear cached data when inputs change
    useEffect(() => {
        // Clear existing data when inputs change
        setSummaryData(null);
        setSummaryError(null);
        setDetailedData(
            CompareContractsOutput.clone({
                totalSize: 0,
                deltas: []
            })
        );
        setHasComparison(false);
        lastApiInputsRef.current = null;
    }, [leftId, rightId, leftTimestamp, rightTimestamp]);

    // Retry function for summary generation
    const retrySummaryGeneration = useCallback(() => {
        loadSummaryData();
    }, [loadSummaryData]);

    const runComparison = useCallback(() => {
        if (isNil(leftId) || isNil(rightId)) {
            return;
        }

        const currentInputs = { leftId, rightId, leftTimestamp, rightTimestamp };

        // Only call API if inputs have changed or no detailed data exists
        if (!inputsChanged(currentInputs) && hasComparison) {
            console.log('Detailed inputs unchanged, using cached data');
            return;
        }

        setLoading(true);
        setError(false);

        const input = CompareContractsInput.clone({
            left: { contractId: leftId, timestamp: leftTimestamp },
            right: { contractId: rightId, timestamp: rightTimestamp },
            includeMatchingRows: true,
            includeSummary: false
        });

        getFullComparison(input)
            .then((result) => {
                const validationErrors = validateComparison(result);
                if (validationErrors.length === 0) {
                    setHasComparison(true);
                    setDetailedData(result);
                    lastApiInputsRef.current = currentInputs; // Update shared input tracking
                } else {
                    setToasts((prev) => {
                        return validationErrors.map((e, index) => ({
                            id: `compare-contracts-${index}`,
                            message: e,
                            type: 'error'
                        }));
                    });
                    setError(true);
                }
            })
            .catch((e: any) => {
                setError(true);
                if (e instanceof UnknownCoralError) {
                    setTopAlertMsg(e.message);
                } else {
                    setTopAlertMsg(`Unknown error: ${JSON.stringify(e)}`);
                }
                setDetailedData(
                    CompareContractsOutput.clone({
                        totalSize: 0,
                        deltas: []
                    })
                );
            })
            .finally(() => {
                setLoading(false);
            });
    }, [leftId, leftTimestamp, rightId, rightTimestamp, inputsChanged, hasComparison, setToasts, setTopAlertMsg]);

    // Handler for tab change
    const handleTabChange = useCallback(
        (newTab: ValidTab) => {
            console.log(newTab);
            setCurrentTab(newTab);
            // Load summary data when switching to summary tab (includes input change check)
            if (newTab === COMPARE_BY_SUMMARY && leftId && rightId) {
                loadSummaryData();
            }
        },
        [loadSummaryData, leftId, rightId]
    );

    // Handler for Compare button click
    const handleCompareClick = useCallback(() => {
        runComparison();
        if (currentTab === COMPARE_BY_SUMMARY) {
            loadSummaryData();
        }
    }, [runComparison, currentTab, loadSummaryData]);

    // Load both summary and detailed data when contracts change
    useEffect(() => {
        runComparison();
        if (currentTab === COMPARE_BY_SUMMARY) {
            loadSummaryData();
        }
    }, [leftId, leftTimestamp, rightId, rightTimestamp, runComparison, loadSummaryData, currentTab]);

    let statusComponent = <></>;

    if (loading) {
        statusComponent = <Loader type={'circular'} size={'medium'} />;
    } else if (error) {
        statusComponent = (
            <Alert type={'error'} size={'medium'}>
                Error
            </Alert>
        );
    } else if (!hasComparison) {
        statusComponent = (
            <Alert type={'neutral'} size={'medium'}>
                Please enter contract IDs
            </Alert>
        );
    } else if (exactMatch) {
        statusComponent = (
            <Alert type={'success'} size={'medium'}>
                Exact Match!
            </Alert>
        );
    } else {
        statusComponent = (
            <Alert type={'neutral'} size={'medium'}>
                Found {differences} difference{differences > 1 && 's'}
            </Alert>
        );
    }

    let comparisonComponent = <></>;

    if (currentTab === COMPARE_BY_SUMMARY) {
        comparisonComponent = (
            <CompareBySummary
                summaryData={summaryData}
                loading={summaryLoading}
                error={summaryError}
                onFallbackToTable={() => setCurrentTab(COMPARE_BY_TABLE)}
                onRetry={retrySummaryGeneration}
                canRetry={summaryError ? isRetryableError({ message: summaryError }) : false}
                leftId={leftId}
                rightId={rightId}
                leftTimestamp={leftTimestamp}
                rightTimestamp={rightTimestamp}
                contractIdMap={contractIdMap}
            />
        );
    } else if (currentTab === COMPARE_BY_TABLE) {
        comparisonComponent = (
            <Row width={'100%'} widths={['fill']}>
                {!exactMatch && <CompareByTable comparison={detailedData} formatters={formatters} />}
            </Row>
        );
    } else if (currentTab === COMPARE_SIDE_BY_SIDE) {
        comparisonComponent = <SideBySideViewer leftId={leftId} rightId={rightId} />;
    }

    return (
        <>
            <Column heights={['fit', 'fit', 'fill']} width={'100%'}>
                <Row widths={['fit', 'fit', 'fit', 'fill']}>
                    <ContractSelector
                        side={'left'}
                        deals={dealsResponse?.contractDeals}
                        entities={entitiesResponse?.entities}
                        updateId={setLeftId}
                        updateTimestamp={setLeftTimestamp}
                    />
                    <Column
                        alignmentVertical={'top'}
                        heights={['fit', 'fill']}
                        height={'100%'}
                        alignmentHorizontal={'center'}
                    >
                        <Button onClick={handleCompareClick} disabled={loading || summaryLoading}>
                            <Icon tokens={rightArrowTokens} />
                            Compare!
                            <Icon tokens={leftArrowTokens} />
                        </Button>
                    </Column>
                    <ContractSelector
                        side={'right'}
                        deals={dealsResponse?.contractDeals}
                        entities={entitiesResponse?.entities}
                        updateId={setRightId}
                        updateTimestamp={setRightTimestamp}
                    />
                    <Row>{statusComponent}</Row>
                </Row>
                <TabGroup value={currentTab} onChange={handleTabChange}>
                    <Tab value={COMPARE_BY_SUMMARY}>
                        <Row spacing="m" alignmentVertical="center">
                            <Text>Summary</Text>
                            <Icon tokens={sparklesSmallIcon} />
                        </Row>
                    </Tab>
                    <Tab value={COMPARE_BY_TABLE}>Compare by Table</Tab>
                    <Tab value={COMPARE_SIDE_BY_SIDE}>View Side by Side</Tab>
                </TabGroup>
                {comparisonComponent}
            </Column>
        </>
    );
};

function getTitle(
    leftId: number | undefined,
    rightId: number | undefined,
    contractIdMap: Map<string, string> | undefined
): string {
    if (!isUndefined(leftId) && !isUndefined(rightId)) {
        const left = contractIdMap?.get(`${leftId}`) ?? leftId;
        const right = contractIdMap?.get(`${rightId}`) ?? rightId;

        return `Compare Contracts: ${left} vs ${right}`;
    } else {
        return 'Compare Contracts';
    }
}
